import { useMemo } from 'react';

import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';

import { CreateFiatWalletSteps } from './type.ts';

const staticBackTargets: Record<
  CreateFiatWalletSteps,
  CreateFiatWalletSteps | undefined
> = {
  [CreateFiatWalletSteps.WHATS_NEW]: undefined,
  [CreateFiatWalletSteps.ACTIVATE_YOUR_CASH_WALLET]: undefined,
  [CreateFiatWalletSteps.HANDLE_ID_REVIEW_ERROR]: undefined,
  [CreateFiatWalletSteps.INTRO]: undefined,
  [CreateFiatWalletSteps.TELL_US_ABOUT_YOU]: CreateFiatWalletSteps.INTRO,
  [CreateFiatWalletSteps.MANGOPAY_SCA]: CreateFiatWalletSteps.INTRO,
  [CreateFiatWalletSteps.CHOOSE_CURRENCY]:
    CreateFiatWalletSteps.TELL_US_ABOUT_YOU,
  [CreateFiatWalletSteps.REVIEW_INFORMATION]:
    CreateFiatWalletSteps.CHOOSE_CURRENCY,
  [CreateFiatWalletSteps.CHOOSE_DOCUMENT]:
    CreateFiatWalletSteps.ACTIVATION_SUCCESS,
  [CreateFiatWalletSteps.HOSTED_IDENTITY_VERIFICATION]:
    CreateFiatWalletSteps.ACTIVATION_SUCCESS,
  [CreateFiatWalletSteps.UPLOAD]: CreateFiatWalletSteps.CHOOSE_DOCUMENT,
  [CreateFiatWalletSteps.REVIEW_INFO_BEFORE_ADDING_ID]:
    CreateFiatWalletSteps.INTRO,
  [CreateFiatWalletSteps.DOCUMENT_UNDER_REVIEW]: undefined,
  [CreateFiatWalletSteps.IDENTITY_VERIFICATION_RESULT]: undefined,
  [CreateFiatWalletSteps.ACTIVATION_SUCCESS]: undefined,
};

const retryFromReviewSteps = [
  CreateFiatWalletSteps.HANDLE_ID_REVIEW_ERROR,
  CreateFiatWalletSteps.IDENTITY_VERIFICATION_RESULT,
];

type Props = {
  initialStep?: CreateFiatWalletSteps;
};
export const useBackButtonTargets = ({ initialStep }: Props) => {
  const { canListAndTrade } = useFiatBalance();
  const isRetryInitialStep =
    !!initialStep && retryFromReviewSteps.includes(initialStep);
  const backTargets = useMemo(
    () => ({
      ...staticBackTargets,
      ...(initialStep &&
        [
          CreateFiatWalletSteps.WHATS_NEW,
          CreateFiatWalletSteps.ACTIVATE_YOUR_CASH_WALLET,
        ].includes(initialStep) && {
          [CreateFiatWalletSteps.INTRO]: initialStep,
        }),
      ...(isRetryInitialStep && {
        [CreateFiatWalletSteps.REVIEW_INFO_BEFORE_ADDING_ID]:
          CreateFiatWalletSteps.HANDLE_ID_REVIEW_ERROR,
        [CreateFiatWalletSteps.CHOOSE_DOCUMENT]:
          CreateFiatWalletSteps.REVIEW_INFO_BEFORE_ADDING_ID,
        [CreateFiatWalletSteps.HOSTED_IDENTITY_VERIFICATION]:
          CreateFiatWalletSteps.REVIEW_INFO_BEFORE_ADDING_ID,
      }),
    }),
    [initialStep, isRetryInitialStep]
  );

  return useMemo(() => {
    if (canListAndTrade && !isRetryInitialStep) {
      return {
        ...backTargets,
        [CreateFiatWalletSteps.CHOOSE_DOCUMENT]: CreateFiatWalletSteps.INTRO,
        [CreateFiatWalletSteps.HOSTED_IDENTITY_VERIFICATION]:
          CreateFiatWalletSteps.INTRO,
      };
    }
    return backTargets;
  }, [canListAndTrade, isRetryInitialStep, backTargets]);
};
